import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Segment } from 'semantic-ui-react';
import { shape, func } from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { getConsultDetails } from 'Redux/modules/webChat/consult/actions';
import { getConsultById } from 'Selectors/webChat/consults';
import { generateIncludeParams } from 'Helpers/utils';
import { CONSULT_COMMONS_MODEL_RESPONSE } from 'Constants/webchat';
import { healthSummaryRetrieve } from 'Redux/modules/webChat/actions';
import { getHealthSummaryById } from 'Selectors/webChat/healthSummary';

import ContentConsult from './ContentConsult';
import ModalHealthSummary from './ModalHealthSummary';

const consultDetailsResponseParams = {
  include: generateIncludeParams({
    ...CONSULT_COMMONS_MODEL_RESPONSE,
    health_summary: true,
    consult_fee: true,
  }),
};

const ModalBotFinishConsult = props => {
  const { close, message } = props;
  const [isHealthSummaryModalOpen, setHealthSummaryModalOpen] = useState(false);
  const [isConsultLoading, setConsultLoading] = useState(false);
  const [isHealthSummaryLoading, setHealthSummaryLoading] = useState(false);

  const consultId = message.metadata?.consultId;
  const dispatch = useDispatch();

  // Get consult data
  const consult = useSelector(state => getConsultById(state, consultId), shallowEqual);

  // Extract health summary info
  const healthSummaryInfo = consult?.healthSummary?.[0] || {};
  const { healthSummaryId, doctorId } = healthSummaryInfo;

  // Get health summary data
  const healthSummary = useSelector(
    state => getHealthSummaryById(state, healthSummaryId),
    shallowEqual,
  );

  // Fetch consult data
  const fetchConsultDetails = useCallback(async () => {
    setConsultLoading(true);
    try {
      await dispatch(getConsultDetails(consultId, consultDetailsResponseParams));
    } finally {
      setConsultLoading(false);
    }
  }, [consultId, dispatch]);

  // Fetch health summary data
  const fetchHealthSummary = useCallback(async () => {
    if (!healthSummaryId || !doctorId) return;

    setHealthSummaryLoading(true);
    try {
      await dispatch(
        healthSummaryRetrieve({
          summaryId: healthSummaryId,
          doctorId,
        }),
      );
    } finally {
      setHealthSummaryLoading(false);
    }
  }, [dispatch, healthSummaryId, doctorId]);

  // Toggle health summary modal
  const toggleHealthSummaryModal = () => {
    setHealthSummaryModalOpen(prev => !prev);
  };

  // Fetch consult on mount
  useEffect(() => {
    fetchConsultDetails();
  }, [fetchConsultDetails]);

  // Fetch health summary when IDs are available
  useEffect(() => {
    if (healthSummaryId && doctorId) {
      fetchHealthSummary();
    }
  }, [healthSummaryId, doctorId, fetchHealthSummary]);

  const isLoading = isConsultLoading || isHealthSummaryLoading;

  return (
    <Modal open closeOnDimmerClick size="tiny" onClose={close} closeIcon>
      <Modal.Header>Consultation Details</Modal.Header>
      <Modal.Content as={Segment} basic loading={isLoading} style={{ minHeight: 200 }}>
        {!isLoading && consult && (
          <ContentConsult
            consult={consult}
            openHS={toggleHealthSummaryModal}
            healthSummary={healthSummary}
          />
        )}
        {isHealthSummaryModalOpen && (
          <ModalHealthSummary
            onClose={toggleHealthSummaryModal}
            healthSummaries={consult?.healthSummary || []}
            healthSummary={healthSummary}
          />
        )}
      </Modal.Content>
    </Modal>
  );
};

ModalBotFinishConsult.propTypes = {
  message: shape({}).isRequired,
  close: func.isRequired,
};

export default ModalBotFinishConsult;
